/* assets/component-home-video-showcase.js
 *
 * Custom Element <home-video-showcase>.
 *
 * Behavior:
 * - Lazy-defers video load via IntersectionObserver. Only when the section enters
 *   the viewport do we set <video> src and trigger play.
 * - Respects prefers-reduced-motion: if user opts out, autoplay is disabled and
 *   the fallback image stays visible.
 *
 * Configured via data attributes on the wrapping <home-video-showcase>:
 *   data-video-url   - the video file URL (Shopify file URL OR external)
 *
 * No global state. Communicates nothing externally.
 */

class HomeVideoShowcase extends HTMLElement {
  connectedCallback() {
    this._videoUrl = this.dataset.videoUrl || '';
    this._videoEl = this.querySelector('video');
    if (!this._videoEl || !this._videoUrl) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Respect reduced motion - leave fallback image only.
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersect.bind(this), {
      rootMargin: '200px 0px',
      threshold: 0.01,
    });
    this._observer.observe(this);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  _onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this._loadAndPlay();
        this._observer.disconnect();
      }
    }
  }

  _loadAndPlay() {
    if (!this._videoEl || !this._videoUrl) return;
    if (this._videoEl.src) return; // already loaded
    this._videoEl.src = this._videoUrl;
    this._videoEl.load();
    const playPromise = this._videoEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Some browsers block autoplay; fail silently - fallback image stays.
      });
    }
  }
}

if (!customElements.get('home-video-showcase')) {
  customElements.define('home-video-showcase', HomeVideoShowcase);
}
